import { Grid, Typography } from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ReplyIcon from '@material-ui/icons/Reply';
import Warning from '@material-ui/icons/Warning';
import { TextInput } from '@ncr-retail/base-retail-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import { TenderType } from 'src/common/enumerations/tender-type';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { POSModeState } from 'src/common/interfaces/states/POSModeState';
import { ReactComponent as CashIcon } from 'src/common/media/Cash.svg';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { ITenderExchangeWindowProps } from './tender-exchange-window';
import { tenderOptionsContainerStyles } from './tender-options-styles';

const TenderOptions = (props: ITenderExchangeWindowProps): JSX.Element => {
  const { t } = useTranslation();
  const trainingModeClasses = trainingModeStyles();
  const leftHanded = useSelector((state: AppState): boolean => state.cashierPreferences.leftHanded);
  const posModeState = useSelector((state: AppState): POSModeState => state.posMode);
  const classes = tenderOptionsContainerStyles();

  const { onCancel, onBack, paymentTender } = props;

  const inputIcon = (isError: boolean): { icon: JSX.Element } | undefined =>
    isError ? { icon: <Warning /> } : undefined;

  const titleSubHeaderIcon = (): JSX.Element | null => {
    if (paymentTender?.type === TenderType.Cash) {
      return <CashIcon className={classes.subHeaderIcon} />;
    }

    if (
      paymentTender?.type === TenderType.CreditDebit ||
      paymentTender?.type === TenderType.CheckWithFee
    ) {
      return <CreditCardIcon className={classes.subHeaderIcon} />;
    }

    return null;
  };

  const { minValue, maxValue, multiplesOf } = props.tenderExchange;
  const hasValueValidation = minValue || maxValue || multiplesOf;
  return (
    <Grid
      item
      container
      className={`fullHeight flexFull ${classes.outerMostContainer}`}
      direction={leftHanded ? 'row-reverse' : 'row'}
      data-testid='tender-options-screen-container'
    >
      <Grid
        item
        className={`fullWidth ${
          posModeState.trainingMode
            ? trainingModeClasses.trainingModeBackground
            : classes.background
        }`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight fullWidth ${classes.outerFlexDesign}`}
          data-testid='tender-options-container'
        >
          <Grid
            item
            container
            className={`${classes.flexDesign} ${classes.containerForm}`}
            direction={leftHanded ? 'row-reverse' : 'row'}
            data-testid='tender-exchange-body-container'
          >
            <Grid data-testid='tender-option-form' className={'fullWidth'}>
              <Grid container className={classes.containerSubheader}>
                <Grid container direction='row' className={`${classes.containerTitleSubheader}`}>
                  {titleSubHeaderIcon()}
                  <Typography
                    className={classes.returnItemTitleSubheader}
                    data-testid='tender-option-title'
                    variant='subtitle1'
                  >
                    {paymentTender && t(paymentTender.description ?? 'Tender')}
                  </Typography>
                </Grid>
                {hasValueValidation && (
                  <Grid
                    container
                    direction='row'
                    className={`${classes.containerTextAmount} ${classes.outerFlexDesign}`}
                    data-testid='tender-options-limits-content'
                  >
                    {multiplesOf ? (
                      <Typography
                        className={classes.textAmount}
                        variant='body1'
                        data-testid='tender-option-multipleOf'
                      >
                        {t('AmountMultipleOf', { arg: multiplesOf })}
                      </Typography>
                    ) : null}
                    {minValue ? (
                      <Typography
                        className={classes.textAmount}
                        variant='body1'
                        data-testid='tender-option-min-value'
                      >
                        {`${t('TenderOptionsMin')} ${convertCurrency(minValue)}`}
                      </Typography>
                    ) : null}
                    {maxValue ? (
                      <Typography
                        className={classes.textAmount}
                        variant='body1'
                        data-testid='tender-option-max-value'
                      >
                        {`${t('TenderOptionsMax')} ${convertCurrency(maxValue)}`}
                      </Typography>
                    ) : null}
                  </Grid>
                )}
              </Grid>
              <Grid
                container
                direction='row'
                className={`${classes.containerAmountField} ${classes.outerFlexDesign}`}
              >
                <Grid item>
                  <Typography
                    className={classes.textAmountLabel}
                    variant='body1'
                    data-testid='tender-option-amount'
                  >
                    {`${t('Amount')} ${
                      paymentTender?.currency?.symbol ? `(${paymentTender?.currency?.symbol})` : ''
                    }`}
                  </Typography>
                </Grid>
                <Grid item>
                  <TextInput
                    type='text'
                    helperText={props.amountInputElement.helperText}
                    value={props.amountInputElement.name}
                    error={!!props.amountInputElement.helperText}
                    onChange={props.onTextChange}
                    autoFocus={true}
                    focused={true}
                    autoComplete='off'
                    id='amount-value'
                    name='amount-value'
                    inputIcon={inputIcon(!!props.amountInputElement.helperText)}
                    className={`formInputHeight ${classes.inputAmount}`}
                    inputProps={{
                      'data-testid': 'tender-exchange-amount-value',
                      'data-keyboard-owner': 'tender-exchange-focus',
                    }}
                    FormHelperTextProps={{
                      className: classes.helper,
                    }}
                    onKeyDown={(e: any) => {
                      e.key === 'Enter' ? props.onKeypadSubmit(e.target.value) : null;
                    }}
                    inputRef={props.inputRef}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid xs item container justifyContent='center' className={classes.bottomActions}>
            <BaseButton
              buttonType='white'
              onClick={onBack}
              data-testid='tender-exchange-back-button'
              className={classes.buttonBack}
              startIcon={<ReplyIcon />}
            >
              {t('GoBackCommand')}
            </BaseButton>
            <BaseButton
              buttonType='white'
              onClick={onCancel}
              className={classes.buttonCancel}
              data-testid='tender-exchange-cancel-button'
            >
              {t('Cancel')}
            </BaseButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default TenderOptions;
